<template>
  <v-dialog
    v-model="openDialog"
    persistent="persistent"
    fullscreen="fullscreen"
    eager="eager"
    transition="dialog-bottom-transition"
  >
    <v-toolbar :color="toolbarColor" dark="dark">
      <v-icon class="mr-2" v-html="toolbarIcon"></v-icon><span>Voucher Info</span>
      <v-spacer></v-spacer>
      <v-btn text="text" @click="openDialog = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card>
      <v-container fluid="fluid">
        <v-row>
          <v-col cols="3">
            <v-row>
              <v-col cols="4">
                <base-card title="Voucher" :text="data.voucher_no"></base-card>
              </v-col>
              <v-col cols="4">
                <base-card title="Date" :text="data.date.dateFormat()"></base-card>
              </v-col>
              <v-col cols="4">
                <base-card title="Status" :text="status"></base-card>
              </v-col>
              <v-col cols="6">
                <base-card title="Project Code" :text="projectCode"></base-card>
              </v-col>
              <v-col cols="6">
                <base-card title="Budget Code" :text="budgetCode"></base-card>
              </v-col>
              <v-col cols="6">
                <base-card title="Created By" :text="requestor"></base-card>
              </v-col>
              <v-col cols="6">
                <base-card title="Paid By" :text="paidBy"></base-card>
              </v-col>
              <v-col cols="6">
                <base-card title="Cost Center" :text="costCenter"></base-card>
              </v-col>
              <v-col cols="6">
                <base-card title="Pay To" :text="payTo"></base-card>
              </v-col>
              <v-col cols="12">
                <base-card title="Address" :text="data.address"></base-card>
              </v-col>
              <v-col cols="12" v-if="!approvedOrRejected">
                <v-btn
                  class="mr-2"
                  color="green"
                  dense="dense"
                  small="small"
                  dark="dark"
                  :loading="approving"
                  @click="$emit('approve', data.id)"
                  ><span>Approve</span></v-btn
                >
                <v-btn
                  color="red"
                  dense="dense"
                  small="small"
                  dark="dark"
                  :loading="rejecting"
                  @click="$emit('reject', data.id)"
                  ><span>Reject</span></v-btn
                >
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="5">
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Account Code</th>
                    <th>Reason</th>
                    <th class="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in data.details" :key="item.id">
                    <td>
                      <div v-if="item.account_code">
                        <span class="primary--text mr-2">{{ item.account_code.account_code }}</span
                        ><small class="blue-grey--text">{{ item.account_code.description }}</small>
                      </div>
                    </td>
                    <td>{{ item.reason }}</td>
                    <th class="text-right">{{ item.amount.phpFormat() }}</th>
                  </tr>
                  <tr>
                    <th colspan="2">Total</th>
                    <th class="text-right">{{ totalAmount.phpFormat() }}</th>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
          <v-col cols="4">
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Acct Code</th>
                    <th class="text-right">Disbursed Amount</th>
                    <th class="text-right">Amount</th>
                    <th class="text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in data.account_codes" :key="item.id">
                    <td>
                      <div v-if="item.account_code">
                        <span class="primary--text mr-2">{{ item.account_code.account_code }}</span
                        ><small class="blue-grey--text">{{ item.account_code.description }}</small>
                      </div>
                    </td>
                    <th class="text-right">{{ Number(item.disbursed_amount).phpFormat() }}</th>
                    <th class="text-right">{{ item.amount.phpFormat() }}</th>
                    <th class="text-right" :class="(item.balance &lt; 0 &amp;&amp; 'red--text')">
                      {{ item.balance.phpFormat() }}
                    </th>
                  </tr>
                  <tr v-if="data.revolving_fund">
                    <th>--.--</th>
                    <th class="text-right">{{ revolvingFundDisbursedAmount.phpFormat() }}</th>
                    <th class="text-right">{{ (0).phpFormat() }}</th>
                    <th class="text-right red--text">
                      -{{ revolvingFundDisbursedAmount.phpFormat() }}
                    </th>
                  </tr>
                  <tr>
                    <th>Total</th>
                    <th class="text-right">{{ totalDisbursedAmount.phpFormat() }}</th>
                    <th class="text-right">{{ totalAmount.phpFormat() }}</th>
                    <th class="text-right">{{ totalBalance.phpFormat() }}</th>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>
<script>
import { defineComponent } from 'vue'

/**
 *
 * @param {string} code
 * @param {string} description
 */
function codeAndDescriptionTemplate(code, description) {
  return `<span class="primary--text mr-2">${code}</span><small class="blue-grey--text">${description}</small>`
}

export default defineComponent({
  name: 'ShowVoucher',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    approving: {
      type: Boolean,
      default: false,
    },
    rejecting: {
      type: Boolean,
      default: false,
    },
  },
  // props: ['value', 'data', 'approving', 'rejecting'],
  data() {
    return {}
  },
  computed: {
    toolbarIcon() {
      if (this.data.voucher_status_id === 'AP') return 'mdi-check'
      if (this.data.voucher_status_id === 'RJ') return 'mdi-alert'
      return 'mdi-info-outlined'
    },
    toolbarColor() {
      if (this.data.voucher_status_id === 'AP') return 'success'
      if (this.data.voucher_status_id === 'RJ') return 'error'
      return 'primary'
    },
    approvedOrRejected() {
      const status = this.data.voucher_status_id
      return ['AP', 'RJ'].includes(status)
    },
    approvedRejectedBy() {
      if (!this.approvedOrRejected) return null
      return this.data.approved_rejected_by.name
    },
    requestor() {
      if (!this.data.created_by) return null
      return this.data.created_by.name
    },
    costCenter() {
      if (!this.data.cost_center) return null
      const { code, description } = this.data.cost_center
      return codeAndDescriptionTemplate(code, description)
    },
    paidBy() {
      if (!this.data.paid_by) return null
      const { code, description } = this.data.paid_by
      return codeAndDescriptionTemplate(code, description)
    },
    payTo() {
      if (!this.data.pay_to) return null
      const { id, name } = this.data.pay_to
      return codeAndDescriptionTemplate(id, name)
    },
    totalDisbursedAmount() {
      return (
        this.data.account_codes.reduce((total, item) => Number(item.disbursed_amount) + total, 0) +
        this.revolvingFundDisbursedAmount
      )
    },
    totalBalance() {
      return (
        this.data.account_codes.reduce((total, item) => item.balance + total, 0) -
        this.revolvingFundDisbursedAmount
      )
    },
    totalAmount() {
      if (!this.data.details) return 0
      return this.data.details.reduce((total, item) => total + item.amount, 0)
    },
    revolvingFundDisbursedAmount() {
      return this.data?.revolving_fund?.disbursed_amount || 0
    },
    status() {
      const template = (c, a, b) =>
        `<span class="${c}--text mr-2">${a}</span><small class="blue-grey--text">${b}</small>`
      const dataStatus = this.data.status
      if (dataStatus.id === 'AP')
        return template('success', dataStatus.description, this.approvedRejectedBy)
      if (dataStatus.id === 'RJ')
        return template('error', dataStatus.description, this.approvedRejectedBy)
      return `<span class="primary--text">${dataStatus.description}</span>`
    },
    projectCode() {
      if (!this.data.project_code) return null
      const pc = this.data.project_code
      return `
        <span class="primary--text">${pc.project_code}</span><br>
        <small class="blue-grey--text">${pc.description}</small>`
    },
    budgetCode() {
      if (!this.data.budget_code) return null
      const bc = this.data.budget_code
      return `
        <span class="primary--text">${bc.budget_code}</span><br>
        <small class="blue-grey--text">${bc.description}</small>`
    },
    openDialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
  },
  methods: {},
})
</script>
